import ImageWithFallback from '@zeptonow/zui/ImageWithFallback';
import { generateCDNImageUrl } from '@zeptonow/zui/utils';
import { EventConstants } from '../constants/events';
import { CDNBaseURL, S3AssetUrl } from '../constants/global';
import {
  ModalContainerDataInterface,
  SideDrawerContainerDataInterface,
  ToastContainerDataInterface,
} from '../hooks/types';
import {
  DebounceInterface,
  GenerateImageInterface,
  GenerateImageSourcePropsInterface,
} from './types';

export const generateImageSource = (
  props: GenerateImageSourcePropsInterface
) => {
  const { path, baseURL = CDNBaseURL, noScale } = props;
  return generateCDNImageUrl({
    relativePath: path,
    baseUrl: baseURL ?? '',
    noScale,
  });
};
export const generateImage: GenerateImageInterface = ({
  name,
  alt,
  className,
  extension = 'svg',
  fullSource = false,
  cdnSource = 'PUBLIC',
  noScale = false,
}) => {
  return (
    <ImageWithFallback
      src={generateCDNImageUrl({
        relativePath: fullSource ? name : `assets/icons/${name}.${extension}`,
        baseUrl: cdnSource === 'PUBLIC' ? CDNBaseURL : S3AssetUrl,
        noScale,
      })}
      alt={alt}
      className={className}
    />
  );
};

export const debounce: DebounceInterface = (func: any, timeout = 300) => {
  let timer: any;
  return (...args: any) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
};

export const closeSideDrawer = () => {
  document.dispatchEvent(
    new CustomEvent<SideDrawerContainerDataInterface>(
      EventConstants.HIDE_SIDEDRAWER
    )
  );
};

export const openSideDrawer = (props: SideDrawerContainerDataInterface) => {
  document.dispatchEvent(
    new CustomEvent<SideDrawerContainerDataInterface>(
      EventConstants.SHOW_SIDEDRAWER,
      {
        detail: {
          ...props,
        },
      }
    )
  );
};

export const openToast = (props: ToastContainerDataInterface) => {
  document.dispatchEvent(
    new CustomEvent<ToastContainerDataInterface>(EventConstants.SHOW_TOAST, {
      detail: {
        ...props,
      },
    })
  );
};

export const openModal = (props: ModalContainerDataInterface) => {
  document.dispatchEvent(
    new CustomEvent<ModalContainerDataInterface>(EventConstants.SHOW_MODAL, {
      detail: {
        ...props,
      },
    })
  );
};

export const closeModal = () => {
  document.dispatchEvent(
    new CustomEvent<ModalContainerDataInterface>(EventConstants.HIDE_MODAL)
  );
};

export const setFallbackImage = (e: any, fallbackSrc: any) => {
  e.target.onerror = null; // prevent looping
  e.target.src = fallbackSrc;
  e.target.style = 'width: 25%;';
};

export const getImageUrlFromImage = ({
  image_path,
  scalePercentage = 0.5,
  inputType = 'WIDTH',
  cdnSource = 'S3',
  noScale = false,
}: {
  image_path: string;
  scalePercentage?: number;
  inputType?: 'WIDTH' | 'HEIGHT';
  cdnSource?: 'S3' | 'PUBLIC';
  noScale?: boolean;
}): string => {
  if (image_path) {
    return `${cdnSource === 'S3' ? S3AssetUrl : CDNBaseURL}${
      !noScale ? `/tr:w-${scalePercentage},h-${scalePercentage}/` : '/'
    }${image_path}`;
  } else {
    return '';
  }
};

export const renderPriceWithRupeeSymbol = (
  value: string | undefined | null | number
) => {
  return typeof value !== 'undefined' && value !== null
    ? Number(value)?.toLocaleString('en-IN', {
        maximumFractionDigits: 2,
        style: 'currency',
        currency: 'INR',
      })
    : value;
};

export const formatPrice = (value: number) => {
  if (typeof value === 'undefined' || value === null) {
    return '-';
  }
  if (value >= 10000000)
    return `${(value / 10000000)?.toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      style: 'currency',
      currency: 'INR',
    })} Cr`;
  if (value >= 100000)
    return `${(value / 100000)?.toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      style: 'currency',
      currency: 'INR',
    })} L`;
  return value?.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    style: 'currency',
    currency: 'INR',
  });
};

export const formatNumber = (value: number) => {
  if (typeof value === 'undefined' || value === null) {
    return '-';
  }
  if (value >= 10000000)
    return `${(value / 10000000)?.toLocaleString('en-IN', {
      maximumFractionDigits: 2,
    })} Cr`;
  if (value >= 100000)
    return `${(value / 100000).toLocaleString('en-IN', {
      maximumFractionDigits: 2,
    })} L`;
  return value?.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

export const getClassName = (
  classNames:
    | string
    | Array<
        string | number | undefined | null | Record<string, boolean | undefined>
      >
    | Record<string, boolean | undefined>
    | undefined
    | null
): string => {
  return getClassNameHelper(classNames).join(' ');
};

const getClassNameHelper = (
  classNames:
    | string
    | number
    | Array<
        string | number | undefined | null | Record<string, boolean | undefined>
      >
    | Record<string, boolean | undefined>
    | undefined
    | null
): string[] => {
  if (!classNames) {
    return [];
  }
  if (Array.isArray(classNames)) {
    return classNames.reduce(
      (arr: string[], item) => [...arr, ...getClassNameHelper(item)],
      []
    );
  } else if (typeof classNames === 'object') {
    return Object.keys(classNames).reduce(
      (arr: string[], key: string) => (classNames[key] ? [...arr, key] : arr),
      []
    );
  }
  return [String(classNames)];
};

export function getExtensionFromFileName(filename: string) {
  const ext = filename.split('.').pop();
  if (ext === filename) return '';
  return ext || '';
}

export const convertValuesToString = (
  obj: Record<string, any>
): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const key in obj) {
    const value = obj[key];
    if (Array.isArray(value)) {
      if (value.every((item) => typeof item === 'object' && item !== null)) {
        const arrayString = value
          .map((item) => JSON.stringify(item).replace(/"/g, '""'))
          .join(', ');
        result[key] = `"${arrayString}"`;
      } else {
        result[key] = `"${value.join(', ').replace(/"/g, '""')}"`;
      }
    } else if (typeof value === 'object' && value !== null) {
      result[key] = `"${JSON.stringify(value).replace(/"/g, '""')}"`;
    } else if (typeof value === 'string') {
      result[key] = `"${value.replace(/"/g, '""')}"`;
    } else {
      result[key] = value;
    }
  }

  return result;
};

export const tablePageSize = Math.floor(window.innerHeight / 50);

const camelToNormalCase = (camelCase: string): string => {
  // Insert space before each capital letter and convert the first letter to uppercase
  const normalCase = camelCase.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Capitalize the first letter
  return normalCase.charAt(0).toUpperCase() + normalCase.slice(1);
};

export const formatObjectKeysToNormalCase = (
  obj: Record<string, any>
): Record<string, any> => {
  const formattedObj: Record<string, any> = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const normalCaseKey = camelToNormalCase(key);
      formattedObj[normalCaseKey] = obj[key]; // Keep the value as is
    }
  }

  return formattedObj;
};
